import {
  type CanvasElementFrom,
  type IoConfig,
  type PartialPropsOf,
  type PropsOf,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import { type FontWeightOption } from '@blaze-shared/api-contracts-domain';

import { FormLikeFieldValidationHandler } from '../../canvas-elements/io-data/default-handlers/formlike-field-validation-handler.service';
import { registerElementDefinition } from '../../services/element-definitions.service';

import { DatetimeInputDefinitionMapper } from './ai/datetime-input.definition-mapper';
import { DatetimeInputElementHandler } from './ai/datetime-input.element-handler';
import { DatetimeInputComponent } from './datetime-input.component';
import { DatetimeInputPropsBuilder } from './datetime-input.props-builder';
import { DEFAULT_WIDTH, getPropsConfig, MIN_SIZE } from './datetime-input.props-config';
import { type DatetimeInputPropsModel } from './datetime-input.props-model';
import { generateDatetimeInputFilterField } from './generation/filter-field';
import { generateDatetimeInputFormField } from './generation/form-field';
import { datetimeInputV2Migration } from './migrations/002-datetime-input.migration';
import { datetimeInputV3Migration } from './migrations/003-datetime-input.migration';
import { datetimeInputV4Migration } from './migrations/004-datetime-input.migration';
import { datetimeInputV5PaddingToMarginMigration } from './migrations/005-datetime-input-padding-to-margin.migration';

export { transformToDatetimeInput } from './datetime-input.transforms';
export type DatetimeInputElement = CanvasElementFrom<'datetimeInput'>;
export type DatetimeInputDef = WithProps<DatetimeInputPropsModel> & WithIoConfig;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    datetimeInput: DatetimeInputDef;
  }
}

registerElementDefinition({
  type: 'datetimeInput',
  componentType: DatetimeInputComponent,
  validationHandler: FormLikeFieldValidationHandler,
  isListed: true,
  name: 'Date Time',
  width: { mode: 'value', value: DEFAULT_WIDTH, fixed: true },
  height: { mode: 'value', value: MIN_SIZE, fixed: false },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  defaults: getDefaults(),
  initial: getInitial(),
  propsBuilder: DatetimeInputPropsBuilder,
  generation: {
    filterField: generateDatetimeInputFilterField,
    formField: generateDatetimeInputFormField,
  },
  groups: ['custom'],
  styleIncludePaths: [
    'style',
    'content.title.align',
    'content.title.iconPosition',
    'content.title.position',
    'content.title.width',
  ],
  metaUiFeatures: { type: 'input' },
  features: {
    searchAndFiltersFieldSupport: true,
    ai: {
      optionalInput: true,
    },
  },
  ai: {
    name: 'datetimeInput',
    handler: () => DatetimeInputElementHandler,
    definitionMapper: () => DatetimeInputDefinitionMapper,
  },

  version: 5,
  migrations: {
    2: datetimeInputV2Migration,
    3: datetimeInputV3Migration,
    4: datetimeInputV4Migration,
    5: datetimeInputV5PaddingToMarginMigration,
  },
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        bindingDefs: [{ property: 'value', label: 'Value', dataType: 'DateTime' }],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [
      {
        eventType: 'change',
        debounce: 500,
        schema: {
          // While the default data type is a DateTime, this can change later to a Date or Time if properties of the filed changes
          columns: [
            {
              columnType: 'DateTime',
              dataType: 'DateTime',
              name: '', // for non-object value schema, this should be empty
              isProfilePicture: false,
            },
          ],
          id: '',
          name: 'Date Time',
        },
      },
    ],
  };
}

function getDefaults(): PropsOf<'datetimeInput'> {
  return {
    content: {
      title: {
        enabled: true,
        label: 'Date Time',
        position: 'left',
        align: 'left',
        width: '30%',
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
      date: {
        enabled: true,
        format: 'MM/DD/YYYY',
        min: '',
        max: '',
        defaultValue: 'none',
        defaultValueFunction: 'startOfMonth',
        disableDatepicker: false,
      },
      time: {
        enabled: true,
        format: '12h',
        min: '',
        max: '',
        defaultTime: '',
        defaultValue: 'none',
        defaultValueFunction: 'startOfDay',
        defaultValueOffset: '0 min',
      },
    },
    style: {
      fontSize: '$t.fontSize',
      fontFamily: '$t.fontFamily',
      lineHeight: '$t.lineHeight',
      general: {
        title: { color1: '$t.title', selectorType: 'solid' },
        accent: { color1: '$t.accent', selectorType: 'solid' },
        offset: { margin: '$t.spacingInput' },
      },
      labelBlock: {
        fontFamily: '$t.fontFamily',
        fontSize: '$t.fontSize',
        fontStyle: [],
        fontWeight: '$t.labelFontWeight',
        iconSize: '16px',
        lineHeight: '$t.lineHeight',
      },
      inputFont: {
        style: [],
        fontFamily: '$t.fontFamily',
        fontSize: '$t.fontSize',
        fontWeight: '$t.fontWeight' as FontWeightOption,
      },
      input: {
        value: { color1: '$t.value', selectorType: 'solid' },
        fill: { color1: '$t.fillInternal', selectorType: 'solid' },
        border: { color1: '$t.outline', selectorType: 'solid' },
        radius: '$t.radius',
      },
    },
  };
}

function getInitial(): PartialPropsOf<'datetimeInput'> {
  return {
    content: {
      title: {
        width: '35%',
      },
    },
  };
}
